import type { AppliedLimitThreshold } from '@/lib/api/generated/model';

/**
 * What is known about one limit, and how it came to be known.
 *
 * A union rather than an optional state, so a caller cannot read a threshold
 * that was never determined. `known: false` is the ordinary condition for a
 * member of a workspace, because the usage figures are administrator-only; it
 * is not a fault, must never render as an error, and must never be treated as
 * "under the limit" (spec 101 FR-032, research R4).
 *
 * `source` decides what a surface may claim. A refused run can only ever prove
 * `reached`; only the figures can distinguish `approaching`.
 */
export type LimitState =
  | { known: false }
  | {
      known: true;
      state: AppliedLimitThreshold;
      source: 'refusal' | 'figures';
    };

const NOT_KNOWN: LimitState = { known: false };

/** The privileged reading: the workspace's state and each capped workflow's. */
export interface LimitFigures {
  workspace: AppliedLimitThreshold;
  byWorkflow: ReadonlyMap<string, AppliedLimitThreshold>;
}

/** The free reading: which scopes have actually refused a run. */
export interface LimitRefusals {
  workspace: boolean;
  workflows: ReadonlySet<string>;
}

export interface ResolveLimitStateInput {
  /** Null when the figures were refused, failed, or have not answered. */
  figures: LimitFigures | null;
  refusals: LimitRefusals;
  /** The workflow in question, when a surface is asking about one. */
  workflowId: string | undefined;
}

export interface ResolvedLimits {
  workspace: LimitState;
  workflow: LimitState;
}

function resolve(
  fromFigures: AppliedLimitThreshold | undefined,
  refused: boolean,
): LimitState {
  // The figures win, and not merely because they say more. A refusal is a fact
  // about a moment that has passed: the cap may have been raised or the period
  // rolled since. The figures are the current reading from the same authority
  // that enforces the limit (research R1).
  if (fromFigures)
    return { known: true, state: fromFigures, source: 'figures' };
  if (refused) return { known: true, state: 'exhausted', source: 'refusal' };
  return NOT_KNOWN;
}

/**
 * One answer per scope, from whichever sources are available.
 *
 * Pure, so the rule it encodes can be read and tested without a workspace, a
 * query client or a rendered tree.
 */
export function resolveLimitState({
  figures,
  refusals,
  workflowId,
}: ResolveLimitStateInput): ResolvedLimits {
  return {
    workspace: resolve(figures?.workspace, refusals.workspace),
    workflow:
      workflowId === undefined
        ? NOT_KNOWN
        : resolve(
            figures?.byWorkflow.get(workflowId),
            refusals.workflows.has(workflowId),
          ),
  };
}

/** True when this scope will refuse the next run (research R2). */
export function isReached(state: LimitState): boolean {
  return state.known && state.state === 'exhausted';
}
